import type { Appearance, StripeElementsOptions } from '@stripe/stripe-js';

import { getDocument } from '@/lib/utils';

const DEFAULT_DIMENSIONS = {
  borderRadius: '6px',
  fontLineHeight: '1.3',
  fontSizeXs: '12px',
  fontSizeSm: '14px',
  fontWeightNormal: '400',
  spacingUnit: '4px',
} as const;

const VARIABLE_TOKEN_MAP = {
  colorPrimary: '--color-primary',
  colorPrimaryText: '--color-primary-foreground',
  colorBackground: '--color-card',
  colorText: '--color-foreground',
  colorDanger: '--color-destructive',
  colorTextPlaceholder: '--color-muted-foreground',
  fontFamily: '--font-sans',
} as const;

const DEFAULT_VARIABLES = {
  colorPrimary: '#1656BE',
  colorPrimaryText: '#ffffff',
  colorBackground: '#ffffff',
  colorText: '#171717',
  colorDanger: '#de3c3f',
  colorTextPlaceholder: '#a3a3a3',
  fontFamily: 'Inter, ui-sans-serif, sans-serif, system-ui',
  borderRadius: DEFAULT_DIMENSIONS.borderRadius,
  fontLineHeight: DEFAULT_DIMENSIONS.fontLineHeight,
  fontSizeXs: DEFAULT_DIMENSIONS.fontSizeXs,
  fontSizeSm: DEFAULT_DIMENSIONS.fontSizeSm,
  fontWeightNormal: DEFAULT_DIMENSIONS.fontWeightNormal,
  spacingUnit: DEFAULT_DIMENSIONS.spacingUnit,
} as const;

function getTokenValue(token: string, fallback: string): string {
  const root = getDocument();

  if (!root) {
    return fallback;
  }

  const value = root.defaultView
    ?.getComputedStyle(root.documentElement)
    .getPropertyValue(token)
    .trim();

  return value && value !== 'initial' ? value : fallback;
}

function resolveAppearanceVariables(): Appearance['variables'] {
  const resolved: Record<string, string> = {};

  for (const [key, token] of Object.entries(VARIABLE_TOKEN_MAP)) {
    if (!token) continue;

    const typedKey = key as keyof typeof DEFAULT_VARIABLES;
    const fallback = DEFAULT_VARIABLES[typedKey];
    if (typeof fallback === 'string') {
      const value = getTokenValue(token, fallback);
      resolved[typedKey] = value;
    }
  }

  for (const [key, value] of Object.entries(DEFAULT_VARIABLES)) {
    if (!(key in VARIABLE_TOKEN_MAP) && typeof value === 'string') {
      resolved[key] = value;
    }
  }

  return resolved as Appearance['variables'];
}

function buildAppearanceRules(): Appearance['rules'] {
  const inputBorder = getTokenValue('--color-input', '#d4d4d4');
  const hoverBorder = getTokenValue('--color-border', '#d4d4d4');
  const focusRing = getTokenValue('--color-ring', DEFAULT_VARIABLES.colorPrimary);

  return {
    '.Input': {
      border: `1px solid ${inputBorder}`,
    },
    '.Input:focus': {
      border: `1px solid ${focusRing}`,
      boxShadow: `0 0 0 3px ${hoverBorder}, 0 0 0 1px ${hoverBorder}`,
      outline: 'none',
    },
  } satisfies Appearance['rules'];
}

export function createStripeElementsOptions(): StripeElementsOptions {
  const variables = resolveAppearanceVariables();

  return {
    mode: 'setup',
    currency: 'usd',
    paymentMethodTypes: ['card'],
    paymentMethodCreation: 'manual',
    appearance: {
      theme: 'stripe',
      labels: 'floating',
      variables,
      rules: buildAppearanceRules(),
    },
  } satisfies StripeElementsOptions;
}
